import React, { useEffect, useRef } from 'react';
import { C } from '../../shared/design-tokens.js';

interface SafetySignatureCanvasProps {
  resetKey: string | number;
  onSignatureChange: (data: string | null) => void;
  height?: number;
  minStrokeLength?: number;
}

export default function SafetySignatureCanvas({
  resetKey,
  onSignatureChange,
  height = 150,
  minStrokeLength = 50,
}: SafetySignatureCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const totalStrokeRef = useRef(0);

  function paintBlank() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  function getCanvasPoint(event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in event && event.touches.length > 0) {
      return {
        x: (event.touches[0].clientX - rect.left) * scaleX,
        y: (event.touches[0].clientY - rect.top) * scaleY,
      };
    }

    const mouseEvent = event as React.MouseEvent<HTMLCanvasElement>;
    return {
      x: (mouseEvent.clientX - rect.left) * scaleX,
      y: (mouseEvent.clientY - rect.top) * scaleY,
    };
  }

  function startDraw(event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawingRef.current = true;
    const point = getCanvasPoint(event);
    lastPointRef.current = point;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  }

  function draw(event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    ctx.strokeStyle = C.navy;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    const dx = point.x - lastPointRef.current.x;
    const dy = point.y - lastPointRef.current.y;
    totalStrokeRef.current += Math.sqrt(dx * dx + dy * dy);
    lastPointRef.current = point;
  }

  function endDraw() {
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (totalStrokeRef.current >= minStrokeLength) {
      onSignatureChange(canvas.toDataURL('image/png'));
      return;
    }
    onSignatureChange(null);
  }

  function clearSignature() {
    totalStrokeRef.current = 0;
    paintBlank();
    onSignatureChange(null);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = 800;
    canvas.height = Math.max(200, Math.round(height * 2));
    totalStrokeRef.current = 0;
    paintBlank();
    onSignatureChange(null);
  }, [height, minStrokeLength, onSignatureChange, resetKey]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height,
          borderRadius: 16,
          border: `1px solid ${C.borderSoft}`,
          background: '#ffffff',
          touchAction: 'none',
          cursor: 'crosshair',
          display: 'block',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <button
        type="button"
        onClick={clearSignature}
        className="btn btn-ghost"
        style={{ marginTop: 10 }}
      >
        Clear signature
      </button>
    </div>
  );
}
